import router from '@/router/index';
import store from '@/store';
import { log as debugLog } from '@/utils/debugLogger';

export const VIEWERS_MODE = {
	namespaced: true,
	state: {
		lastRoute: null,
		options: {
			button: null,
			mode: null,
			metadata: null,
			action: null,
			panoramaTransfer: null,
		},
		// Back-restore payload: written by EXIT_VIEWER_MODE from
		// options.metadata.taskRestore (or similar) before the options reset,
		// so it survives long enough for the prior page's mounted hook to
		// consume it. Cleared by the consumer.
		pendingRestore: null,
	},
	getters: {
		OPTIONS(state) {
			return state.options;
		},
		IS_AVAILABLE(state) {
			return state.lastRoute !== null;
		},
		IS_TASK_COLORING_AVAILABLE(state) {
			return state.options.mode === 'task-coloring';
		}
	},
	mutations: {
		setLastRoute(state, route) {
			state.lastRoute = route;
		},
		setOptions(state, options) {
			state.options = options;
		},
		setPendingRestore(state, payload) {
			state.pendingRestore = payload;
		}
	},
		actions: {
		START_VIEWER_MODE({ commit }, { mode, viewer, button, metadata, action, panoramaTransfer }) {
			debugLog('[VIEWERS_MODE/START] called', {
				mode, viewer, button, action,
				hasMetadata: !!metadata,
				hasTaskRestore: !!(metadata?.taskRestore),
				taskRestore: metadata?.taskRestore ?? null,
				hasPanoramaTransfer: !!panoramaTransfer,
				panoramaTransfer: panoramaTransfer ?? null,
			});
			store.commit('SET_VIEW_1_TYPE', viewer);
			store.commit('SET_VIEW_2_TYPE', '');
			store.commit('SET_VIEW_3_TYPE', '');
			store.commit('SET_VIEW_4_TYPE', '');
			store.commit('SET_IS_VIEW_1_VISIBLE', true);
			store.commit('SET_IS_VIEW_2_VISIBLE', false);
			store.commit('SET_IS_VIEW_3_VISIBLE', false);
			store.commit('SET_IS_VIEW_4_VISIBLE', false);

			commit('setLastRoute', router.history.current);
			commit('setOptions', {
				button,
				viewer,
				mode,
				metadata,
				action,
				panoramaTransfer: panoramaTransfer ?? null,
			});

			router.push({ name: 'facility' });
		},
		EXIT_VIEWER_MODE({ commit, state }) {
			debugLog('[VIEWERS_MODE/EXIT] entered', {
				hasPanoramaTransfer: !!state.options.panoramaTransfer,
				hasTaskRestore: !!(state.options.metadata?.taskRestore),
				taskRestore: state.options.metadata?.taskRestore ?? null,
				currentPendingRestore: state.pendingRestore,
			});

			// 360°-expand bridge cleanup: when the expand flow set selectedImage
			// and tagIdToHighlight as bridge state to fullscreen, clear them on
			// exit so the inline page returns to its pre-expand Vuex state.
			// Gated on panoramaTransfer so other exit paths (3D/2D fullscreen,
			// AI Inspect) are unaffected.
			if (state.options.panoramaTransfer) {
				store.commit('SET_SELECTED_IMAGE', null);
				store.commit('setTagIdToHighlight', null);
				debugLog('[VIEWERS_MODE/EXIT] cleared selectedImage + tagIdToHighlight');
			}

			// Back-restore handoff: move the consumer-specific restore payload
			// from options.metadata (about to be cleared) to the top-level
			// pendingRestore field. The prior page's mounted hook reads it.
			if (state.options.metadata?.taskRestore) {
				commit('setPendingRestore', state.options.metadata.taskRestore);
				debugLog('[VIEWERS_MODE/EXIT] wrote pendingRestore', state.options.metadata.taskRestore);
			}

			// Safety-net: if user backs out before the 3D viewer's geometry-loaded
			// callback fires, clear it so the next viewer load doesn't isolate
			// this task's parts unexpectedly. Gated on viewerMode === 'tasks' so
			// this clear only fires for our own task-expand deep-link flow —
			// preserving callbacks set by other concurrent flows (panorama
			// loading at forgeService.js:3294, area loading at :3486, URL tag
			// isolation at MainView.vue:770). Mirrors the panoramaTransfer gate
			// pattern used 20 lines above.
			if (
				state.options.metadata?.viewerMode === 'tasks' &&
				store.state.forgeService?.on3dModelLoadedCallback
			) {
				debugLog('[VIEWERS_MODE/EXIT] safety-net clearing on3dModelLoadedCallback (task flow)');
				store.state.forgeService.on3dModelLoadedCallback = null;
			}

			store.commit('SET_AMIS_SEARCH_TEXT', '');
			commit('setLastRoute', null);
			commit('setOptions', {
				button: null,
				mode: null,
				metadata: null,
				action: null,
				panoramaTransfer: null,
			});
			// Note: pendingRestore is intentionally NOT cleared here — the
			// consumer (Task's mount hook) clears it after consumption.
			debugLog('[VIEWERS_MODE/EXIT] options cleared, pendingRestore=', state.pendingRestore);
		},
		CLEAR_SCOPED_FILTER({ commit, state }) {
			if (!state.options.metadata?.hasFilter) return;
			commit('setOptions', {
				...state.options,
				metadata: {
					...state.options.metadata,
					hasFilter: false,
					filterRequestId: null,
					tags: [],
					tagIds: [],
				},
			});
		},
		BACK_TO_PAGE({ state, dispatch }) {
			debugLog('[VIEWERS_MODE/BACK_TO_PAGE] called, lastRoute=', state.lastRoute?.name);
			store.commit('SET_AMIS_SEARCH_TEXT', '');
			router.push(state.lastRoute);
			dispatch('EXIT_VIEWER_MODE');
		}
	},
}
